// Normalize custom_options to an array of groups.
// Old format: { customHandle, showImages, options } → wrap in array
// New format: [{ customHandle, showImages, options }, ...] → return as-is
export function getCustomOptionGroups(customOptions) {
    if (!customOptions) return [];
    if (Array.isArray(customOptions)) return customOptions;
    if (customOptions.customHandle !== undefined) return [customOptions];
    return [];
}

// Compute total price modifier for the given selections.
// serializedCustomOption: plain string (old, single-group) or JSON string of { [groupHandle]: optionLabel }
export function getCustomOptionPriceModifier(customOptions, serializedCustomOption, currency) {
    if (!serializedCustomOption || !customOptions) return 0;
    const groups = getCustomOptionGroups(customOptions);
    if (groups.length === 0) return 0;

    let selections = null;
    try {
        const parsed = JSON.parse(serializedCustomOption);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            selections = parsed;
        }
    } catch {}

    let total = 0;
    if (selections) {
        // New multi-group format
        groups.forEach(group => {
            const selectedLabel = selections[group.customHandle];
            const opt = (group.options || []).find(o => o.label === selectedLabel);
            if (opt?.priceModifier?.[currency]) total += parseFloat(opt.priceModifier[currency]);
        });
    } else {
        // Old format: plain string label, search all groups
        groups.forEach(group => {
            const opt = (group.options || []).find(o => o.label === serializedCustomOption);
            if (opt?.priceModifier?.[currency]) total += parseFloat(opt.priceModifier[currency]);
        });
    }
    return total;
}

// Serialize selections object to cart-compatible string.
// Single group → plain string. Multiple groups → JSON string.
export function serializeCustomOptionSelections(customOptions, selectionsObj) {
    const groups = getCustomOptionGroups(customOptions);
    if (groups.length === 0 || Object.keys(selectionsObj).length === 0) return null;
    if (groups.length === 1) return selectionsObj[groups[0].customHandle] ?? null;
    return JSON.stringify(selectionsObj);
}

// Pick the DISPLAY label for the active language, falling back to the canonical
// (BA) value. The canonical `label` is NEVER changed — it stays the key used for
// stock/price/cart/order matching. Optional `label_us`/`label_ru` siblings only
// change what the shopper sees.
export function pickLabel(obj, language, base = 'label') {
    if (!obj) return '';
    if (language === 'US') return obj[base + '_us'] || obj[base] || '';
    if (language === 'RU') return obj[base + '_ru'] || obj[base] || '';
    return obj[base] || '';
}

// Display name for an option group. The canonical `customHandle` stays the key;
// optional `name_us`/`name_ru` provide localized headings.
export function pickGroupName(group, language) {
    if (!group) return '';
    if (language === 'US') return group.name_us || group.customHandle || '';
    if (language === 'RU') return group.name_ru || group.customHandle || '';
    return group.customHandle || '';
}

// Get a human-readable summary of selections for display, localized to `language`.
export function formatCustomOptionDisplay(customOptions, serializedCustomOption, language = 'BA') {
    if (!serializedCustomOption) return null;
    const groups = getCustomOptionGroups(customOptions);

    let selections = null;
    try {
        const parsed = JSON.parse(serializedCustomOption);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) selections = parsed;
    } catch {}

    if (selections) {
        return Object.entries(selections).map(([handle, label]) => {
            const group = groups.find(g => g.customHandle === handle);
            const opt = (group?.options || []).find(o => o.label === label);
            return `${pickGroupName(group, language) || handle}: ${opt ? pickLabel(opt, language) : label}`;
        }).join(', ');
    }
    // Old plain-string format: find the option across groups to localize it
    for (const g of groups) {
        const opt = (g.options || []).find(o => o.label === serializedCustomOption);
        if (opt) return pickLabel(opt, language);
    }
    return serializedCustomOption; // plain string fallback
}
